import type { InvestigationCatalogueItem } from "$global-providers/rxdb-provider/collections/investigation-catalogue-item";
import { randomId } from "$routes/_lib/random-id";
import type { InvestigationFormItem } from "$routes/patient/[:patientId]/investigations/new/validation-schema";
import {
  convertOrderedSchema,
  type OrderedJSONSchema,
  parseSchema,
} from "@elephant-healthcare/yupify";
import { clone } from "ramda";
import type * as Yup from "yup";
import type { RecommendedInvestigation } from "./add-investigation-card";

export type TranslatedInvestigationCatalogueItem = Omit<
  InvestigationCatalogueItem,
  "title" | "keywords"
> & {
  title: string;
  keywords: Array<string>;
};

export const parseCatalogueItemSchema = (
  catalogueItem: TranslatedInvestigationCatalogueItem,
): Yup.AnyObjectSchema => {
  if (!catalogueItem.result) {
    const error = new Error(
      `No result schema for investigation with coding: ${
        catalogueItem.coding?.loinc?.code ??
        catalogueItem.coding?.elephant?.code
      }`,
    );
    throw error;
  }

  const nonOrderedSchema = convertOrderedSchema(
    catalogueItem.result.resultSchema as OrderedJSONSchema,
  );
  return parseSchema(clone(nonOrderedSchema));
};
export const generateBlankInvestigation = (
  idOverride?: string,
): InvestigationFormItem => ({
  id: idOverride ?? randomId(),
  catalogueItem: null,
  outcome: null,
});

export const generatePopulatedInvestigation = (
  catalogueItem: TranslatedInvestigationCatalogueItem,
) => {
  const outcomeSchema = parseCatalogueItemSchema(catalogueItem);
  const defaultOutcome = outcomeSchema.getDefault();
  return {
    id: randomId(),
    catalogueItem,
    outcome: defaultOutcome,
    isRequested: true,
  };
};

export const generateUpdatedInvestigation = ({
  formInvestigation,
  catalogueItem,
}: {
  formInvestigation: InvestigationFormItem;
  catalogueItem: TranslatedInvestigationCatalogueItem;
}) => {
  const outcomeSchema = parseCatalogueItemSchema(catalogueItem);
  const defaultOutcome = outcomeSchema.getDefault();
  return {
    id: formInvestigation.id,
    catalogueItem,
    outcome: defaultOutcome,
    isRequested: true,
  };
};

export const generateUpdatedInvestigationFromRecommendation = ({
  recommendedInvestigation,
  translatedInvestigationList,
  formInvestigation,
}: {
  recommendedInvestigation: RecommendedInvestigation;
  translatedInvestigationList: TranslatedInvestigationCatalogueItem[];
  formInvestigation: InvestigationFormItem;
}) => {
  const catalogueItem = translatedInvestigationList.find(
    (item) =>
      item.coding?.loinc?.code ===
      recommendedInvestigation.catalogueItemLoincCode,
  );
  return catalogueItem
    ? generateUpdatedInvestigation({ formInvestigation, catalogueItem })
    : null;
};
